import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { startOfMonth, endOfMonth, format } from 'date-fns'

export interface CalendarEvent {
  id: string
  title: string
  description?: string
  location?: string
  start: string
  end: string
  attendees: string[]
  html_link?: string
}

interface UseCalendarEventsReturn {
  events: CalendarEvent[]
  loading: boolean
  error: string | null
  fetchEvents: (date: Date, assistantId: string) => Promise<void>
  getEventsForDate: (date: Date) => CalendarEvent[]
  clearEvents: () => void
}

export const useCalendarEvents = (): UseCalendarEventsReturn => {
  const { user } = useAuth()
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const accountId = user?.account_id

  const fetchEvents = useCallback(async (date: Date, assistantId: string) => {
    if (!accountId || !supabase) {
      setError('Conta nao identificada')
      return
    }

    if (!assistantId) {
      setEvents([])
      setError(null)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.access_token) {
        setError('Sessao expirada')
        setLoading(false)
        return
      }

      const monthStart = startOfMonth(date)
      const monthEnd = endOfMonth(date)

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

      const requestBody = {
        account_id: accountId,
        assistant_id: assistantId,
        action: 'list_events',
        params: {
          time_min: monthStart.toISOString(),
          time_max: monthEnd.toISOString(),
          max_results: 100
        }
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/google-calendar-api`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify(requestBody)
      })

      const result = await response.json()

      if (!response.ok) {
        if (result.code === 'NOT_CONNECTED') {
          setEvents([])
          setError(null)
        } else {
          setError(result.error || 'Erro ao buscar eventos')
        }
        setLoading(false)
        return
      }

      setEvents(result.events || [])
      setError(null)
    } catch (err) {
      console.error('[useCalendarEvents] Error:', err)
      setError('Erro ao buscar eventos')
    } finally {
      setLoading(false)
    }
  }, [accountId])

  const getEventsForDate = useCallback((date: Date): CalendarEvent[] => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return events.filter(event => {
      const eventDate = event.start.split('T')[0]
      return eventDate === dateStr
    })
  }, [events])

  const clearEvents = useCallback(() => {
    setEvents([])
    setError(null)
  }, [])

  return {
    events,
    loading,
    error,
    fetchEvents,
    getEventsForDate,
    clearEvents
  }
}
